import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { getCompanyProfileByUserId } from '../services/companyService';
import { getSeekerProfile } from '../services/seekerProfileService';
import { deleteUser } from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';
import ActivatePackageByEmailModal from '../components/ActivatePackageByEmailModal';

const ManageUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [enrichedUsers, setEnrichedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [deletingUser, setDeletingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [selectedResumeUrl, setSelectedResumeUrl] = useState(null);
  const [selectedResumeName, setSelectedResumeName] = useState('');
  const [showActivateEmailModal, setShowActivateEmailModal] = useState(false);
  const [activateEmailModalTarget, setActivateEmailModalTarget] = useState('');
  const usersPerPage = 20;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const usersData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const userData = { id: doc.id, ...doc.data() };

            // Auto-fetch seeker profile for job seekers
            if (userData.userType === 'jobseeker') {
              try {
                const result = await getSeekerProfile(userData.id);
                if (result.success) {
                  userData.seekerProfile = result.profile;
                }
              } catch (err) {
                console.error(`Error fetching seeker profile for ${userData.id}:`, err);
              }
            }

            return userData;
          })
        );
        setUsers(usersData);
        setEnrichedUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = enrichedUsers.filter(user => {
    let matchesFilter = false;

    if (filter === 'all') {
      matchesFilter = true;
    } else if (filter === 'seekerprofiles') {
      // Show only job seekers with complete profile data
      matchesFilter = user.userType === 'jobseeker' && user.seekerProfile &&
        (user.seekerProfile.education?.length > 0 ||
          user.seekerProfile.experience?.length > 0 ||
          user.seekerProfile.skills?.length > 0);
    } else {
      matchesFilter = user.userType === filter;
    }

    const matchesSearch = searchQuery === '' ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.phone && user.phone.includes(searchQuery));

    return matchesFilter && matchesSearch;
  });

  // Reset to first page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const exportToCSV = async () => {
    if (filter === 'all') return;

    setIsExporting(true);
    try {
      // Fetch details for all users in the filtered list if missing
      const usersToExport = await Promise.all(filteredUsers.map(async (user) => {
        // Clone user to avoid mutating state directly
        const userWithDetails = { ...user };

        // Check if we need to fetch details
        const hasDetails = (user.userType === 'employer' && user.company) ||
          (user.userType === 'jobseeker' && user.seekerProfile);

        if (!hasDetails) {
          try {
            if (user.userType === 'employer') {
              const result = await getCompanyProfileByUserId(user.id);
              if (result.success) userWithDetails.company = result.data;
            } else if (user.userType === 'jobseeker') {
              const result = await getSeekerProfile(user.id);
              if (result.success) userWithDetails.seekerProfile = result.profile;
            }
          } catch (err) {
            console.error(`Error fetching details for user ${user.id}`, err);
          }
        }
        return userWithDetails;
      }));

      let headers = [];
      let csvData = [];

      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        // Escape quotes and wrap in quotes if contains comma, quote or newline
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      if (filter === 'jobseeker' || filter === 'seekerprofiles') {
        headers = [
          'User ID', 'Full Name', 'Email', 'Phone', 'Joined Date',
          'Location', 'Skills', 'Experience Level', 'Expected Salary', 'Notice Period',
          'Current Position', 'Education', 'Experience History', 'Applied For / Preferred Job Roles', 'Resume Available', 'LinkedIn Profile'
        ];

        csvData = usersToExport.map(user => {
          // Format Education
          const education = user.seekerProfile?.education?.map(edu => {
            const parts = [edu.degree, edu.institution, edu.year].filter(Boolean);
            return parts.join(' - ');
          }).join('; ') || '';

          // Format Experience
          const experience = user.seekerProfile?.experience?.map(exp => {
            const duration = `${exp.startDate || ''} to ${exp.isCurrentJob ? 'Present' : (exp.endDate || '')}`;
            return `${exp.jobTitle || 'Role'} at ${exp.company || 'Company'} (${duration})`;
          }).join('; ') || '';

          // Format Salary
          const salary = user.seekerProfile?.preferences?.expectedSalary
            ? `${user.seekerProfile.preferences.expectedSalary.min}-${user.seekerProfile.preferences.expectedSalary.max} ${user.seekerProfile.preferences.expectedSalary.currency}`
            : '';

          return [
            user.id,
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            user.email || '',
            user.phone || '',
            formatDate(user.createdAt),
            user.seekerProfile?.personalInfo?.location || '',
            user.seekerProfile?.skills?.join(', ') || '',
            user.seekerProfile?.preferences?.careerLevel || '',
            salary,
            user.seekerProfile?.preferences?.noticePeriod || '',
            user.seekerProfile?.experience?.[0]?.jobTitle || '',
            education,
            experience,
            user.seekerProfile?.preferences?.targetRoles?.join(', ') || '',
            user.seekerProfile?.resumeURL ? 'Yes' : 'No',
            user.seekerProfile?.personalInfo?.linkedIn || ''
          ].map(escapeCsvField);
        });
      } else if (filter === 'employer') {
        headers = [
          'User ID', 'Contact Person', 'Email', 'Phone', 'Joined Date',
          'Company Name', 'Company Industry', 'Company Size', 'Company Location', 'Company Type', 'Company Website'
        ];

        csvData = usersToExport.map(user => {
          return [
            user.id,
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            user.email || '',
            user.phone || '',
            formatDate(user.createdAt),
            user.company?.companyName || '',
            user.company?.industry || '',
            user.company?.companySize || '',
            user.company?.location || '',
            user.company?.companyType || '',
            user.company?.website || ''
          ].map(escapeCsvField);
        });
      } else {
        // Fallback for other types (agent, team) - minimal data
        headers = ['User ID', 'Full Name', 'Email', 'Phone', 'User Type', 'Joined Date'];
        csvData = usersToExport.map(user => [
          user.id,
          `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          user.email || '',
          user.phone || '',
          user.userType || '',
          formatDate(user.createdAt)
        ].map(escapeCsvField));
      }

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filter}_users_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleUserExpansion = async (userId) => {
    if (expandedUser === userId) {
      // Collapse if already expanded
      setExpandedUser(null);
      return;
    }

    // Expand and load detailed data if not already loaded
    setExpandedUser(userId);

    const user = enrichedUsers.find(u => u.id === userId);
    if (!user) return;

    // Check if detailed data is already loaded
    const hasDetailedData = (user.userType === 'employer' && user.company) ||
      (user.userType === 'jobseeker' && user.seekerProfile);

    if (!hasDetailedData) {
      setLoadingDetails(prev => ({ ...prev, [userId]: true }));

      try {
        let additionalData = {};

        if (user.userType === 'employer') {
          const companyResult = await getCompanyProfileByUserId(userId);
          if (companyResult.success) {
            additionalData.company = companyResult.data;
          }
        } else if (user.userType === 'jobseeker') {
          const seekerResult = await getSeekerProfile(userId);
          if (seekerResult.success) {
            additionalData.seekerProfile = seekerResult.profile;
          }
        }

        // Update the enriched users with the new data
        setEnrichedUsers(prev =>
          prev.map(u => u.id === userId ? { ...u, ...additionalData } : u)
        );
      } catch (error) {
        console.error('Error loading user details:', error);
      } finally {
        setLoadingDetails(prev => ({ ...prev, [userId]: false }));
      }
    }
  };

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Auto-load details for all visible users (with glitch prevention)
  const processedUsers = React.useRef(new Set());

  useEffect(() => {
    currentUsers.forEach(user => {
      // Skip if already processed or currently loading
      if (processedUsers.current.has(user.id) || loadingDetails[user.id]) {
        return;
      }

      const hasDetailedData = (user.userType === 'employer' && user.company) ||
        (user.userType === 'jobseeker' && user.seekerProfile);

      if (!hasDetailedData) {
        processedUsers.current.add(user.id);
        toggleUserExpansion(user.id);
      }
    });
  }, [currentUsers, loadingDetails, toggleUserExpansion]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (user) => {
    // Navigate to admin user profile page
    navigate(`/admin/user/${user.id}`);
  };

  const handleViewResume = (resumeUrl, userName, event) => {
    // Stop propagation to prevent card click
    event.stopPropagation();

    if (!resumeUrl) {
      alert('No resume available for this user.');
      return;
    }

    setSelectedResumeUrl(resumeUrl);
    setSelectedResumeName(userName);
    setResumeModalOpen(true);
  };

  const handleDeleteUser = async (userId, userName, event) => {
    // Stop propagation to prevent card click
    event.stopPropagation();

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${userName}?\n\nThis will permanently delete:\n- User account from database\n- User authentication\n- All associated data\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingUser(userId);

    try {
      const result = await deleteUser(userId);

      if (result.success) {
        // Remove user from local state
        setUsers(prev => prev.filter(u => u.id !== userId));
        setEnrichedUsers(prev => prev.filter(u => u.id !== userId));

        // Close expanded view if this user was expanded
        if (expandedUser === userId) {
          setExpandedUser(null);
        }

        alert('User deleted successfully!');
      } else {
        alert(`Failed to delete user: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('An error occurred while deleting the user. Please try again.');
    } finally {
      setDeletingUser(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">User Management</h1>
            <p className="text-sm sm:text-base text-gray-600">Comprehensive user details and management</p>
          </div>
          <div className="flex flex-wrap gap-3 w-full sm:w-auto">
            <button
              onClick={() => {
                setActivateEmailModalTarget('');
                setShowActivateEmailModal(true);
              }}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-4 py-2.5 rounded-lg shadow-md transition-all flex items-center justify-center gap-2 text-sm"
            >
              ⚡ Activate Package by Email
            </button>
            <button
              onClick={exportToCSV}
              disabled={isExporting || filter === 'all'}
              className={`bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm ${(isExporting || filter === 'all') ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              title={filter === 'all' ? "Please select a specific user type to export data" : "Export to CSV"}
            >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </>
            )}
          </button>
        </div>
      </div>
    </div>

      {/* Filters and Search */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex space-x-2 overflow-x-auto pb-2 md:pb-0">
            {['all', 'jobseeker', 'seekerprofiles', 'employer', 'agent', 'team'].map((type) => {
              let count = 0;
              let label = '';

              if (type === 'all') {
                count = enrichedUsers.length;
                label = 'All';
              } else if (type === 'seekerprofiles') {
                count = enrichedUsers.filter(u =>
                  u.userType === 'jobseeker' && u.seekerProfile &&
                  (u.seekerProfile.education?.length > 0 ||
                    u.seekerProfile.experience?.length > 0 ||
                    u.seekerProfile.skills?.length > 0)
                ).length;
                label = 'Seeker Profiles';
              } else {
                count = enrichedUsers.filter(u => u.userType === type).length;
                label = type === 'employer' ? 'Company' : type.charAt(0).toUpperCase() + type.slice(1);
              }

              return (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${filter === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>

          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-6">
        {currentUsers.map((user) => (
          <div
            key={user.id}
            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
            onClick={() => handleCardClick(user)}
          >
            {/* Job Seeker Card - Enhanced attractive design */}
            {user.userType === 'jobseeker' && user.seekerProfile ? (
              <div className="p-4 sm:p-6 bg-gradient-to-br from-blue-50 via-white to-purple-50">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex gap-4 flex-1 min-w-0 w-full">
                    {/* Avatar with gradient */}
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl flex-shrink-0 shadow-lg ring-4 ring-blue-100">
                      {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name */}
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {user.firstName} {user.lastName}
                      </h3>

                      {/* Quick Stats - Experience, Salary, Location */}
                      <div className="flex items-center gap-3 text-sm mb-4 flex-wrap">
                        {user.seekerProfile.experience && user.seekerProfile.experience.length > 0 && user.seekerProfile.experience[0]?.yearsOfExperience && (
                          <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200">
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                              <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                            </svg>
                            <span className="font-medium text-gray-700">{user.seekerProfile.experience[0].yearsOfExperience}</span>
                          </div>
                        )}
                        {user.seekerProfile.preferences?.expectedSalary && (
                          <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200">
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">{(user.seekerProfile.preferences.expectedSalary.min / 100000).toFixed(1)} Lakh</span>
                          </div>
                        )}
                        {user.seekerProfile.personalInfo?.location && (
                          <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200">
                            <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">{user.seekerProfile.personalInfo.location}</span>
                          </div>
                        )}
                      </div>

                      {/* Current/Latest Position */}
                      {user.seekerProfile.experience && user.seekerProfile.experience.length > 0 && (
                        <div className="mb-3 bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-500 mb-1">Current / Latest</p>
                              <p className="text-sm text-gray-900">
                                <span className="font-semibold text-gray-900">{user.seekerProfile.experience[0].jobTitle}</span>
                                <span className="text-gray-500"> at </span>
                                <span className="font-semibold text-blue-600">{user.seekerProfile.experience[0].company}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Education */}
                      {user.seekerProfile.education && user.seekerProfile.education.length > 0 && (
                        <div className="mb-3 bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-500 mb-1">Education</p>
                              <p className="text-sm text-gray-900">
                                {user.seekerProfile.education[0].degree}, <span className="font-semibold text-indigo-600">{user.seekerProfile.education[0].fieldOfStudy}</span>, {user.seekerProfile.education[0].institution}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Skills & Languages in grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        {/* Skills */}
                        {user.seekerProfile.skills && user.seekerProfile.skills.length > 0 && (
                          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-3 border border-orange-100">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-md flex items-center justify-center">
                                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <p className="text-xs font-semibold text-gray-700">Skills</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {user.seekerProfile.skills.slice(0, 4).map((skill, idx) => (
                                <span key={idx} className="bg-white text-orange-700 px-2 py-0.5 rounded-md text-xs font-medium border border-orange-200">
                                  {skill}
                                </span>
                              ))}
                              {user.seekerProfile.skills.length > 4 && (
                                <span className="text-xs text-orange-600 font-medium">+{user.seekerProfile.skills.length - 4}</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Languages */}
                        {user.seekerProfile.languages && user.seekerProfile.languages.length > 0 && (
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-100">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-md flex items-center justify-center">
                                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-2zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <p className="text-xs font-semibold text-gray-700">Languages</p>
                            </div>
                            <p className="text-xs text-gray-700">
                              {user.seekerProfile.languages.map((lang, idx) => (
                                <span key={idx} className="inline-block mr-2">
                                  {lang.language} <span className="text-green-600 font-medium">({lang.proficiency})</span>
                                </span>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Applied For / Preferred Job Roles */}
                      {user.seekerProfile.preferences?.targetRoles && user.seekerProfile.preferences.targetRoles.length > 0 && (
                        <div className="mb-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-md flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                              </svg>
                            </div>
                            <p className="text-xs font-semibold text-gray-700">Applied For / पसंतीच्या नोकरी भूमिका</p>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {user.seekerProfile.preferences.targetRoles.slice(0, 3).map((role, idx) => (
                              <span key={idx} className="bg-white text-purple-700 px-2 py-0.5 rounded-md text-xs font-medium border border-purple-200">
                                {role}
                              </span>
                            ))}
                            {user.seekerProfile.preferences.targetRoles.length > 3 && (
                              <span className="text-xs text-purple-600 font-medium">+{user.seekerProfile.preferences.targetRoles.length - 3}</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Resume Attachment */}
                      {user.seekerProfile.resumeURL && (
                        <div className="mb-3 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-3 border border-cyan-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-500 mb-0.5">Resume / रिज्युमे</p>
                                <p className="text-sm text-gray-900 font-semibold truncate">
                                  {user.firstName}_{user.lastName}_Resume.pdf
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleViewResume(user.seekerProfile.resumeURL, `${user.firstName} ${user.lastName}`, e)}
                              className="ml-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg animate-pulse"
                              title="View Resume"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="text-sm">View</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 pt-3 mt-3 border-t border-gray-200">
                        <div className="bg-blue-50 rounded-lg p-2 min-w-0">
                          <p className="text-xs font-medium text-blue-600 mb-1">Email</p>
                          <p className="text-xs text-gray-700 font-medium truncate break-all">{user.email}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2 min-w-0">
                          <p className="text-xs font-medium text-green-600 mb-1">Phone</p>
                          <p className="text-xs text-gray-700 font-medium break-all">{user.phone || user.seekerProfile.personalInfo?.phone || 'N/A'}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-2 min-w-0">
                          <p className="text-xs font-medium text-purple-600 mb-1">User ID</p>
                          <p className="text-xs text-gray-600 font-mono truncate" title={user.id}>{user.id.substring(0, 10)}...</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`, e)}
                    disabled={deletingUser === user.id}
                    className={`w-full lg:w-auto lg:ml-4 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg ${deletingUser === user.id
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white'
                      }`}
                    title="Delete user"
                  >
                    {deletingUser === user.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span className="text-sm">Deleting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span className="text-sm">Delete</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Enhanced Card Format for Employers, Agents, Team */
              <div className="p-4 sm:p-6 bg-gradient-to-br from-green-50 via-white to-blue-50">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex gap-4 flex-1 min-w-0 w-full">
                    {/* Avatar with gradient */}
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-2xl flex-shrink-0 shadow-lg ring-4 ring-green-100">
                      {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name and User Type Badge */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-gray-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${user.userType === 'employer' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' :
                          user.userType === 'agent' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                            user.userType === 'team' ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' :
                              'bg-gray-200 text-gray-700'
                          }`}>
                          {user.userType === 'employer' ? 'Company' : user.userType?.charAt(0).toUpperCase() + user.userType?.slice(1)}
                        </span>
                      </div>

                      {/* Joined Date */}
                      <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        Joined: {formatDate(user.createdAt)}
                      </p>

                      {/* Company Details for Employers */}
                      {user.userType === 'employer' && user.company && (
                        <>
                          <div className="mb-3 bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 11-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-500 mb-1">Company</p>
                                <p className="text-sm font-bold text-gray-900">{user.company.companyName}</p>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2.5 border border-blue-100">
                              <p className="text-xs font-medium text-blue-600 mb-0.5">Industry</p>
                              <p className="text-sm text-gray-900 font-medium">{user.company.industry}</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-2.5 border border-purple-100">
                              <p className="text-xs font-medium text-purple-600 mb-0.5">Company Size</p>
                              <p className="text-sm text-gray-900 font-medium">{user.company.companySize}</p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-2.5 border border-orange-100">
                              <p className="text-xs font-medium text-orange-600 mb-0.5">Location</p>
                              <p className="text-sm text-gray-900 font-medium">{user.company.location}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-2.5 border border-green-100">
                              <p className="text-xs font-medium text-green-600 mb-0.5">Founded</p>
                              <p className="text-sm text-gray-900 font-medium">{user.company.foundedYear}</p>
                            </div>
                          </div>

                          {user.company.description && (
                            <div className="mb-3 bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg p-3 border border-gray-200">
                              <p className="text-xs font-medium text-gray-600 mb-1">Description</p>
                              <p className="text-sm text-gray-700 line-clamp-2">{user.company.description}</p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Contact Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 pt-3 mt-3 border-t border-gray-200">
                        <div className="bg-blue-50 rounded-lg p-2 min-w-0">
                          <p className="text-xs font-medium text-blue-600 mb-1">Email</p>
                          <p className="text-xs text-gray-700 font-medium truncate break-all">{user.email}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2 min-w-0">
                          <p className="text-xs font-medium text-green-600 mb-1">Phone</p>
                          <p className="text-xs text-gray-700 font-medium break-all">{user.phone || 'N/A'}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-2 min-w-0">
                          <p className="text-xs font-medium text-purple-600 mb-1">User ID</p>
                          <p className="text-xs text-gray-600 font-mono truncate" title={user.id}>{user.id.substring(0, 10)}...</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-2 w-full lg:w-auto lg:ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActivateEmailModalTarget(user.email || '');
                        setShowActivateEmailModal(true);
                      }}
                      className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg font-bold text-xs transition-all flex items-center justify-center gap-1.5 shadow-md"
                      title="Activate Package for this user"
                    >
                      ⚡ Activate Package
                    </button>

                    <button
                      onClick={(e) => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`, e)}
                      disabled={deletingUser === user.id}
                      className={`px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg ${deletingUser === user.id
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white'
                        }`}
                      title="Delete user"
                    >
                      {deletingUser === user.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm">Deleting...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="text-sm">Delete</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {
        totalPages > 1 && filteredUsers.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing <span className="font-semibold">{indexOfFirstUser + 1}</span> to{' '}
                <span className="font-semibold">{Math.min(indexOfLastUser, filteredUsers.length)}</span> of{' '}
                <span className="font-semibold">{filteredUsers.length}</span> users
              </div>

              <div className="flex items-center justify-between sm:justify-center gap-2 w-full sm:w-auto">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors text-sm ${currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Previous
                </button>

                {/* Page Numbers - visible on desktop/tablet, hidden on mobile */}
                <div className="hidden sm:flex items-center gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors text-sm ${currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return (
                        <span key={pageNumber} className="px-2 text-gray-500 text-sm">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Mobile page indicator - hidden on desktop/tablet */}
                <span className="sm:hidden text-sm font-medium text-gray-700 px-2">
                  Page {currentPage} of {totalPages}
                </span>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors text-sm ${currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500">No users match the selected filter.</p>
          </div>
        )
      }

      {/* Resume Viewer Modal */}
      {resumeModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setResumeModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Resume Viewer</h2>
                  <p className="text-sm text-white text-opacity-90">{selectedResumeName}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Download Button */}
                <a
                  href={selectedResumeUrl}
                  download={`${selectedResumeName}_Resume.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  title="Download Resume"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </a>
                {/* Print Button */}
                <button
                  onClick={() => {
                    const printWindow = window.open(selectedResumeUrl, '_blank');
                    if (printWindow) {
                      printWindow.onload = () => {
                        printWindow.print();
                      };
                    }
                  }}
                  className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  title="Print Resume"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>
                {/* Close Button */}
                <button
                  onClick={() => setResumeModalOpen(false)}
                  className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>

            {/* Modal Body - PDF Viewer */}
            <div className="flex-1 overflow-hidden bg-gray-100 rounded-b-2xl">
              {selectedResumeUrl && (
                <iframe
                  src={`${selectedResumeUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  className="w-full h-full border-0"
                  title="Resume Viewer"
                  style={{ minHeight: '600px' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activate Package by Email Modal */}
      <ActivatePackageByEmailModal
        isOpen={showActivateEmailModal}
        initialEmail={activateEmailModalTarget}
        onClose={() => {
          setShowActivateEmailModal(false);
          setActivateEmailModalTarget('');
        }}
      />
    </div>
  );
};

export default ManageUsers;
